import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TrafficLight } from "@/components/TrafficLight";
import { formatRupiah } from "@/lib/format";
import { Calculator, Mountain, GitCompare, Zap, ArrowRight, Ticket, ShieldCheck, TrendingUp } from "lucide-react";

const FEATURES = [
  { icon: Calculator, title: "Hitung Penghematan", desc: "Bandingkan biaya motor listrik vs bensin per bulan hingga 5 tahun." },
  { icon: Mountain, title: "Cek Tanjakan", desc: "Perkirakan apakah kendaraan kemungkinan kuat melewati tanjakan." },
  { icon: GitCompare, title: "Bandingkan Kendaraan", desc: "Sandingkan hingga 3 kendaraan berdasarkan kebutuhan Anda." },
];

const STEPS = [
  { n: "1", t: "Pilih kendaraan", d: "Cari dari katalog motor & sepeda listrik." },
  { n: "2", t: "Masukkan kebutuhan", d: "Jarak harian, rute, dan anggaran Anda." },
  { n: "3", t: "Dapatkan hasil", d: "Kesimpulan mudah dipahami, bukan sekadar angka." },
];

import { AiAsk } from "@/components/AiAsk";
export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.post("/meta/track", { event: "landing_view" }).catch(() => {});
    api.post("/meta/track", { event: "homepage_view" }).catch(() => {});
    api.get("/meta/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/meta/faqs").then((r) => setFaqs(r.data.faqs)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 bg-accent text-teal font-semibold text-sm px-4 py-1.5 rounded-full mb-6">
              <Zap className="w-4 h-4" /> Gratis 2x analisis
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight">
              Sebelum Beli Motor Listrik, <span className="text-teal">Cek Dulu</span> Cocok atau Tidak.
            </h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-xl">
              Hitung penghematan, periksa kekuatan tanjakan, dan bandingkan kendaraan berdasarkan kebutuhan Anda.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-guided-btn" onClick={() => navigate("/bantu-memilih")} className="rounded-full bg-teal hover:bg-teal-hover h-14 px-8 text-base font-semibold shadow-[0_4px_14px_0_rgba(0,191,165,0.39)]">
                Bantu Saya Memilih <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
              <Button data-testid="hero-try-btn" variant="outline" onClick={() => navigate("/register")} className="rounded-full h-14 px-8 text-base font-semibold">
                Coba Gratis
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="relative">
            <div className="absolute inset-0 -z-10 hero-glow rounded-full" />
            <div className="hero-float">
              <img src="https://static.prod-images.emergentagent.com/jobs/7078d075-c588-456a-bd98-08c079972106/images/4e6f2d1de309f486926e8d3586ae3195f57923d00f8871bbbf3af3eff38a13fe.jpeg" alt="Ilustrasi motor listrik modern" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" data-testid="hero-image" />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center"><TrendingUp className="w-6 h-6 text-status-green" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Estimasi hemat / bulan</p>
                <p className="font-heading font-extrabold text-navy text-xl">{formatRupiah(210000)}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-4 relative z-10"><AiAsk /></section>

      {/* FEATURES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title}`} className="bg-white rounded-2xl p-7 border border-border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-4"><f.icon className="w-6 h-6 text-teal" /></div>
              <h3 className="font-heading font-bold text-xl text-navy mb-2">{f.title}</h3>
              <p className="text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="cara-kerja" className="bg-white py-16 border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-navy text-center mb-12">Cara Kerja</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="text-center">
                <div className="w-14 h-14 rounded-full bg-teal text-white font-heading font-extrabold text-2xl flex items-center justify-center mx-auto mb-4">{s.n}</div>
                <h3 className="font-heading font-bold text-xl text-navy mb-2">{s.t}</h3>
                <p className="text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLE RESULT */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-navy rounded-3xl p-8 md:p-12 text-white">
          <p className="text-white/60 text-sm mb-2">Contoh hasil analisis</p>
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl mb-6">E-Skuter A vs Motor Bensin 110cc</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/5 rounded-2xl p-5">
              <p className="text-white/60 text-sm">Penghematan / bulan</p>
              <p className="font-heading font-extrabold text-2xl text-teal">{formatRupiah(210000)}</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-5">
              <p className="text-white/60 text-sm">Balik modal (estimasi)</p>
              <p className="font-heading font-extrabold text-2xl">± 20 bulan</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-5 flex flex-col justify-between">
              <p className="text-white/60 text-sm mb-2">Kesimpulan</p>
              <TrafficLight status="green" />
            </div>
          </div>
          <p className="text-white/40 text-xs mt-6">Hasil merupakan estimasi berdasarkan asumsi, bukan jaminan. Ilustrasi contoh.</p>
        </div>
      </section>

      {/* TRIAL */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-accent rounded-3xl p-8 flex flex-col md:flex-row items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-teal flex items-center justify-center flex-shrink-0"><Ticket className="w-8 h-8 text-white" /></div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading font-bold text-2xl text-navy">3 Kali Analisis Gratis</h3>
            <p className="text-muted-foreground mt-1">Setiap akun baru mendapat 3 kesempatan. Credit hanya berkurang saat analisis berhasil dibuat — request gagal atau membuka katalog tidak mengurangi credit.</p>
          </div>
          <Button data-testid="trial-cta" onClick={() => navigate("/register")} className="rounded-full bg-navy hover:bg-navy/90 h-12 px-6">Mulai Sekarang</Button>
        </div>
      </section>

      {/* PRICING */}
      <section id="harga" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-navy text-center mb-3">Paket Akses Premium</h2>
        <p className="text-muted-foreground text-center mb-10">Lanjutkan analisis tanpa batas setelah trial habis.</p>
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((p) => (
            <div key={p.plan_id} data-testid={`plan-${p.plan_id}`} className={`relative rounded-3xl p-8 border-2 ${p.label ? "border-teal bg-white shadow-xl" : "border-border bg-white"}`}>
              {p.label && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal text-white text-xs font-bold px-4 py-1 rounded-full">{p.label}</span>}
              <h3 className="font-heading font-bold text-xl text-navy">{p.name}</h3>
              <p className="font-heading font-extrabold text-4xl text-navy my-3">{formatRupiah(p.price)}</p>
              <p className="text-muted-foreground text-sm mb-6">Akses semua alat analisis selama {p.duration_days} hari.</p>
              <Button data-testid={`plan-cta-${p.plan_id}`} onClick={() => navigate("/premium")} className={`w-full rounded-full h-12 ${p.label ? "bg-teal hover:bg-teal-hover" : "bg-navy hover:bg-navy/90"}`}>Pilih Paket</Button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-navy text-center mb-10">Pertanyaan Umum</h2>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-list">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-border px-5">
              <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-semibold text-navy hover:no-underline">{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* DISCLAIMER */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4">
          <ShieldCheck className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 leading-relaxed">
            <strong>Disclaimer:</strong> Semua hasil pada platform ini merupakan estimasi berdasarkan asumsi dan data yang tersedia, bukan jaminan performa, penghematan, atau keselamatan. Data kendaraan berasal dari sumber resmi dan daftar pemerintah serta masih diverifikasi bertahap. Selalu lakukan uji langsung dan verifikasi ke penjual resmi sebelum membeli.
          </p>
        </div>
      </section>
    </div>
  );
}
